
import React from 'react';
import { X, FilterX, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FilterOperation } from '@/hooks/useConnectionData';
import { getOperationLabel } from './filters/FilterOperationSelect';

interface FilterValue {
  column: string;
  value: string;
  operation: FilterOperation;
}

interface ActiveFiltersProps {
  filters: FilterValue[];
  onRemoveFilter: (column: string, value: string, operation: FilterOperation) => void;
  onClearAllFilters: () => void;
  isQueryFilters?: boolean;
}

const ActiveFilters: React.FC<ActiveFiltersProps> = ({ 
  filters, 
  onRemoveFilter, 
  onClearAllFilters,
  isQueryFilters = false
}) => {
  if (!filters || filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-md border border-gray-100" data-testid="active-filters">
      <span className="text-sm text-gray-600 font-medium mr-1">
        {isQueryFilters ? (
          <span className="flex items-center gap-1">
            <MessageSquare size={14} />
            Chatbot query filters:
          </span>
        ) : (
          'Active filters:'
        )}
      </span>
      
      <div className="flex flex-wrap gap-2 mt-1">
        {filters.map((filter, index) => (
          <Badge key={index} data-testid={`filter-${index}`} variant="outline" className="flex items-center gap-1 px-2 py-1 bg-white">
            <span className="font-medium">{filter.column}</span>
            <span className="text-muted-foreground">{getOperationLabel(filter.operation)}</span>
            {!['is_blank', 'is_not_blank'].includes(filter.operation) && (
              <span className="font-medium text-blue-600">"{filter.value}"</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1"
              onClick={() => onRemoveFilter(filter.column, filter.value, filter.operation)}
              data-testid={`remove-filter-${index}`}
            >
              <X size={12} />
              <span className="sr-only">Remove filter</span>
            </Button>
          </Badge>
        ))}
      </div>
      
      <Button 
        variant="ghost" 
        size="sm" 
        className="h-7 ml-auto text-xs flex items-center gap-1 text-red-500 hover:text-red-600" 
        onClick={onClearAllFilters}
        data-testid="clear-filters"
      >
        <FilterX size={12} />
        Clear all filters
      </Button>
    </div>
  );
};

export default ActiveFilters;
